import { useEffect, useRef, useState } from 'react';
import { buildQuery, getErrorMessage } from '../../lib/api';
import ProfileAvatar from '../common/ProfileAvatar';
import { useApp } from '../../context/AppContext';
import { formatDateTime } from '../../lib/format';

const REFERENCE_TYPE_META = {
  ONBOARD_TASK: { label: 'Onboard', icon: 'fa-user-plus', color: '#0043ce', bg: '#e8f1ff' },
  ONBOARD_CASE: { label: 'Onboard', icon: 'fa-flag-checkered', color: '#0043ce', bg: '#e8f1ff' },
  OFFBOARD_TASK: { label: 'Offboard', icon: 'fa-user-minus', color: '#da1e28', bg: '#fff1f1' },
  OFFBOARD_CASE: { label: 'Offboard', icon: 'fa-flag-checkered', color: '#da1e28', bg: '#fff1f1' },
  SYSTEM_REGISTER: { label: 'ขออนุมัติระบบ', icon: 'fa-server', color: '#b28600', bg: '#fff8e1' },
  SYSTEM_SCOPE_CHANGE: { label: 'แก้ขอบเขตระบบ', icon: 'fa-sitemap', color: '#7c3aed', bg: '#f3e8ff' },
  ACCESS_REQUEST: { label: 'ขอสิทธิ์', icon: 'fa-key', color: '#198038', bg: '#defbe6' },
};

function describeNotificationContext(item) {
  const ctx = item.context;
  if (!ctx) return null;
  if ((item.referenceType === 'ONBOARD_TASK' || item.referenceType === 'OFFBOARD_TASK') && ctx.empName) {
    const nickname = ctx.empNickname ? ` (${ctx.empNickname})` : '';
    const kind = item.referenceType === 'ONBOARD_TASK' ? 'onboard' : 'offboard';
    return { icon: 'fa-user', text: `${kind} ของ ${ctx.empName}${nickname}${ctx.empCode ? ` · ${ctx.empCode}` : ''}` };
  }
  if (item.referenceType === 'SYSTEM_REGISTER' && ctx.systemName) {
    return { icon: 'fa-server', text: `ระบบ: ${ctx.systemName}` };
  }
  if (item.referenceType === 'SYSTEM_SCOPE_CHANGE' && ctx.systemName) {
    return { icon: 'fa-sitemap', text: `ขอแก้แผนก/ตำแหน่ง: ${ctx.systemName}` };
  }
  return null;
}

export default function Topbar() {
  const {
    activePage,
    apiFetch,
    canCreateSystems,
    currentUser,
    pages,
    refreshUnreadNotifications,
    registrySearch,
    setPendingAction,
    setRegistrySearch,
    showPage,
    showToast,
    unreadCount,
  } = useApp();
  const [searching, setSearching] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const notifRef = useRef(null);

  const handleSearch = async (event) => {
    if (event.key !== 'Enter') return;

    const query = event.currentTarget.value.trim();
    if (!query) return;

    setSearching(true);

    try {
      const response = await apiFetch(`/systems${buildQuery({ search: query, limit: 5 })}`);
      const items = Array.isArray(response.data?.items) ? response.data.items : [];

      setRegistrySearch(query);
      showPage('registry');

      if (items.length > 0) {
        showToast(`พบ ${items.length} รายการที่ตรงกับ "${query}"`);
      } else {
        showToast(`ไม่พบระบบที่ตรงกับ "${query}"`);
      }
    } catch (error) {
      showToast(getErrorMessage(error, 'ค้นหาระบบไม่สำเร็จ'));
    } finally {
      setSearching(false);
      event.currentTarget.value = '';
    }
  };

  const loadNotifications = async () => {
    setNotifLoading(true);
    try {
      const response = await apiFetch(`/notifications${buildQuery({ limit: 10 })}`);
      const items = Array.isArray(response.data?.items) ? response.data.items : [];
      setNotifications(items);
      void refreshUnreadNotifications();
    } catch (error) {
      showToast(getErrorMessage(error, 'โหลดการแจ้งเตือนไม่สำเร็จ'));
    } finally {
      setNotifLoading(false);
    }
  };

  const toggleNotifications = () => {
    setNotifOpen((prev) => {
      const next = !prev;
      if (next) void loadNotifications();
      return next;
    });
  };

  const markRead = async (notificationId) => {
    try {
      await apiFetch(`/notifications/${notificationId}/read`, { method: 'PATCH' });
      setNotifications((prev) => prev.map((item) =>
        item.notificationId === notificationId ? { ...item, isRead: true } : item,
      ));
      void refreshUnreadNotifications();
    } catch (error) {
      showToast(getErrorMessage(error, 'อัปเดตการอ่านไม่สำเร็จ'));
    }
  };

  const routeForNotification = (item) => {
    switch (item.referenceType) {
      case 'ONBOARD_TASK':
      case 'ONBOARD_CASE':
        return 'onboard';
      case 'OFFBOARD_TASK':
      case 'OFFBOARD_CASE':
        return 'offboard';
      case 'SYSTEM_REGISTER':
      case 'SYSTEM_SCOPE_CHANGE':
        return 'approval';
      case 'ACCESS_REQUEST':
        return 'access-request';
      default: return null;
    }
  };

  const resolveTaskContext = async (taskId) => {
    try {
      const response = await apiFetch(`/tasks/${taskId}`);
      const task = response.data || {};
      return { empId: task.empId || null, taskType: task.taskType || null };
    } catch {
      return null;
    }
  };

  const handleNotificationClick = async (item) => {
    if (!item.isRead) void markRead(item.notificationId);
    setNotifOpen(false);
    const target = routeForNotification(item);
    if (!target) return;

    if (
      (item.referenceType === 'ONBOARD_TASK' || item.referenceType === 'OFFBOARD_TASK') &&
      item.referenceId
    ) {
      const empIdFromCtx = item.context?.empId;
      const empId = empIdFromCtx || (await resolveTaskContext(item.referenceId))?.empId;
      if (empId) {
        setPendingAction({
          type: item.referenceType,
          empId,
          taskId: item.referenceId,
        });
      }
    } else if (item.referenceType === 'ONBOARD_CASE' && item.referenceId) {
      const empId = Number(item.referenceId);
      if (Number.isInteger(empId)) {
        setPendingAction({ type: 'ONBOARD_TASK', empId });
      }
    } else if (item.referenceType === 'OFFBOARD_CASE' && item.referenceId) {
      const empId = Number(item.referenceId);
      if (Number.isInteger(empId)) {
        setPendingAction({ type: 'OFFBOARD_TASK', empId });
      }
    } else if ((item.referenceType === 'SYSTEM_REGISTER' || item.referenceType === 'SYSTEM_SCOPE_CHANGE') && item.referenceId) {
      setPendingAction({
        type: item.referenceType,
        systemId: item.context?.systemId || item.referenceId,
      });
    } else if (item.referenceType === 'ACCESS_REQUEST' && item.referenceId) {
      setPendingAction({
        type: 'ACCESS_REQUEST',
        requestId: item.referenceId,
        tab: 'approve',
      });
    }

    showPage(target);
  };

  const markAllRead = async () => {
    try {
      await apiFetch('/notifications/mark-all-read', { method: 'POST' });
      setNotifications((prev) => prev.map((item) => ({ ...item, isRead: true })));
      void refreshUnreadNotifications();
      showToast('ทำเครื่องหมายอ่านแล้วทั้งหมด');
    } catch (error) {
      showToast(getErrorMessage(error, 'อัปเดตไม่สำเร็จ'));
    }
  };

  useEffect(() => {
    if (!notifOpen) return undefined;
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notifOpen]);

  return (
    <div className="topbar">
      <div className="topbar-breadcrumb">
        <span>{pages[activePage] || activePage}</span>
      </div>

      <div className="topbar-actions">
        <div className="search-box" style={{ maxWidth: '260px', marginRight: '8px' }}>
          <span>{searching ? <i className="fa-solid fa-spinner fa-spin"></i> : <i className="fa-solid fa-magnifying-glass"></i>}</span>
          <input
            type="text"
            placeholder={registrySearch ? `ค้นหาต่อจาก "${registrySearch}"` : 'ค้นหาระบบจาก API...'}
            id="global-search"
            onKeyDown={handleSearch}
          />
        </div>

        <div ref={notifRef} style={{ position: 'relative' }}>
          <button
            className="btn btn-ghost btn-sm"
            style={{ position: 'relative', width: '38px', height: '38px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={toggleNotifications}
            aria-label="การแจ้งเตือน"
          >
            <i className="fa-solid fa-bell" style={{ fontSize: '15px' }}></i>
            {unreadCount > 0 && (
              <span style={{ position: 'absolute', top: '-4px', right: '-4px', minWidth: '18px', height: '18px', borderRadius: '999px', background: 'var(--red)', color: '#fff', fontSize: '10px', fontWeight: 700, display: 'grid', placeItems: 'center', padding: '0 5px', border: '2px solid var(--surface)', lineHeight: 1, pointerEvents: 'none' }}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                width: '360px',
                maxHeight: '480px',
                background: '#fff',
                border: '1px solid var(--border)',
                borderRadius: '10px',
                boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
                <strong style={{ fontSize: '14px' }}>การแจ้งเตือน</strong>
                {unreadCount > 0 && (
                  <button className="btn btn-ghost btn-sm" style={{ fontSize: '12px' }} onClick={markAllRead}>
                    อ่านทั้งหมด
                  </button>
                )}
              </div>
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {notifLoading && (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text2)', fontSize: '13px' }}>
                    กำลังโหลด...
                  </div>
                )}
                {!notifLoading && notifications.length === 0 && (
                  <div style={{ padding: '28px 20px', textAlign: 'center', color: 'var(--text3)', fontSize: '13px' }}>
                    ไม่มีการแจ้งเตือน
                  </div>
                )}
                {!notifLoading && notifications.map((item) => {
                  const hasRoute = Boolean(routeForNotification(item));
                  const clickable = hasRoute || !item.isRead;
                  const meta = REFERENCE_TYPE_META[item.referenceType];
                  return (
                    <div
                      key={item.notificationId}
                      onClick={() => clickable && handleNotificationClick(item)}
                      style={{
                        padding: '12px 14px',
                        borderBottom: '1px solid var(--border)',
                        cursor: clickable ? 'pointer' : 'default',
                        background: item.isRead ? 'transparent' : 'rgba(0,67,206,0.04)',
                        display: 'flex',
                        gap: '10px',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(event) => { if (clickable) event.currentTarget.style.background = 'rgba(0,67,206,0.08)'; }}
                      onMouseLeave={(event) => { event.currentTarget.style.background = item.isRead ? 'transparent' : 'rgba(0,67,206,0.04)'; }}
                    >
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.isRead ? 'transparent' : 'var(--red)', marginTop: '6px', flexShrink: 0 }} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        {meta && (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 7px', borderRadius: '999px', background: meta.bg, color: meta.color, fontSize: '10px', fontWeight: 700, marginBottom: '4px' }}>
                            <i className={`fa-solid ${meta.icon}`} style={{ fontSize: '9px' }}></i>
                            {meta.label}
                          </div>
                        )}
                        <div style={{ fontSize: '13px', fontWeight: item.isRead ? 500 : 700, color: 'var(--ink)' }}>
                          {item.title || '(ไม่มีหัวเรื่อง)'}
                        </div>
                        {(() => {
                          const ctx = describeNotificationContext(item);
                          if (!ctx) return null;
                          return (
                            <div style={{ fontSize: '11.5px', color: 'var(--ink)', marginTop: '3px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <i className={`fa-solid ${ctx.icon}`} style={{ fontSize: '10px', color: 'var(--text2)' }}></i>
                              <span>{ctx.text}</span>
                            </div>
                          );
                        })()}
                        {item.message && (
                          <div style={{ fontSize: '12px', color: 'var(--text2)', marginTop: '2px', lineHeight: 1.5 }}>
                            {item.message}
                          </div>
                        )}
                        <div style={{ fontSize: '10.5px', color: 'var(--text3)', marginTop: '4px' }}>
                          {formatDateTime(item.sentAt)}
                          {hasRoute && <span style={{ marginLeft: '8px', color: 'var(--accent)' }}>· กดเพื่อเปิดรายการ</span>}
                        </div>
                      </div>
                      {hasRoute && (
                        <i className="fa-solid fa-chevron-right" style={{ fontSize: '11px', color: 'var(--text3)', alignSelf: 'center', flexShrink: 0 }}></i>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="user-chip" style={{ background: 'var(--surface)', padding: '4px 10px', borderRadius: '999px' }}>
          <ProfileAvatar empCode={currentUser?.empCode} fullName={currentUser?.fullName} size={28} />
          <div className="user-info">
            <div className="user-name" style={{ color: 'var(--ink)' }}>{currentUser?.nameTh || currentUser?.fullName || '-'}</div>
            <div className="user-role" style={{ color: 'var(--text2)' }}>{currentUser?.role || '-'}</div>
          </div>
        </div>

        {canCreateSystems && (
          <button className="btn btn-primary btn-sm" onClick={() => showPage('register-form')}>
            <i className="fa-solid fa-plus"></i> ขึ้นทะเบียนระบบ
          </button>
        )}
      </div>
    </div>
  );
}
